import { type MenuContentCategoryWithSlug, type MenuContentProduct } from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flattens menu categories into list rows: a heading and one row per product,
 * with the last product closing the category spacing. Custom categories
 * collapse into a single row that lays out its own cards.
 */
export function buildMenuContentListRows(params: Params): MenuContentListRow[] {
  const { categories, reorderRailTargetId } = params;

  const reorderRailRows = reorderRailTargetId ? [buildReorderRailRow(reorderRailTargetId)] : [];
  const categoryRows = categories.flatMap(buildCategoryRows);

  return [...reorderRailRows, ...categoryRows];
}

export function getMenuContentListRowTargetId(row: MenuContentListRow) {
  return row.targetId;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildReorderRailRow(targetId: string): MenuContentListRow {
  return { kind: 'reorder-rail', key: targetId, targetId };
}

function buildCategoryRows(
  category: MenuContentCategoryWithSlug,
  categoryIndex: number,
): MenuContentListRow[] {
  const rowBase: CategoryRowBase = { key: category.slug, targetId: category.slug, category, categoryIndex }; // prettier-ignore

  if (category.isCustom) return buildCustomCategoryRows(rowBase);

  const headingRow: MenuContentListRow = { ...rowBase, kind: 'category-heading', key: `${rowBase.key}:heading` }; // prettier-ignore
  const lastProductIndex = category.products.length - 1;
  const productRows = category.products.map<MenuContentListRow>((product, productIndex) => ({
    ...rowBase,
    kind: 'product',
    key: `${rowBase.key}:product:${product.id}`,
    product,
    isLastProduct: productIndex === lastProductIndex,
  }));

  return [headingRow, ...productRows];
}

function buildCustomCategoryRows(rowBase: CategoryRowBase): MenuContentListRow[] {
  const customProducts = rowBase.category.products.filter((product) => product.isCustom);

  if (customProducts.length === 0) return [];

  return [{ ...rowBase, kind: 'custom-products', key: `${rowBase.key}:custom`, products: customProducts }]; // prettier-ignore
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  categories: readonly MenuContentCategoryWithSlug[];
  reorderRailTargetId: string | undefined;
};

type RowBase = { key: string; targetId: string };

type CategoryRowBase = RowBase & {
  category: MenuContentCategoryWithSlug;
  categoryIndex: number;
};

export type MenuContentListRow =
  | (RowBase & { kind: 'reorder-rail' })
  | (CategoryRowBase & { kind: 'category-heading' })
  | (CategoryRowBase & { kind: 'product'; product: MenuContentProduct; isLastProduct: boolean })
  | (CategoryRowBase & { kind: 'custom-products'; products: MenuContentProduct[] });
